import React, { useCallback, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import numeral from 'numeral';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import AddVideoIcon from '@wix/da-ds/pkg/Icons/24x24/AddVideo';
import { Dims } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import { getSrcSet } from '../../Dumbo/Image/_helpers';
import ZoomModal from '../../ZoomModal';
import { scaleToSize } from '../../_helpers';
import { FullViewImageProps } from '../types';
import { useFullview } from '../hooks/useFullview';
import { useImgIsLoading } from '../hooks/useImageIsLoading';
import { useIsMounted } from '@wix/da-hooks/pkg/useIsMounted';

import s from './Image.scss';

export interface Props extends FullViewImageProps {
  altText?: string;
  wrapperProps?: React.HTMLAttributes<HTMLDivElement>;
  imageProps?: React.ImgHTMLAttributes<HTMLImageElement>;
  afterImage?: React.ReactNode;
  allowFullviewSrcsetForPngs?: boolean;
}

export const Image: React.FC<Props> = ({
  source,
  altText = '',
  width,
  height,
  dontExpandWrapper,
  wrapperProps = {},
  imageProps = {},
  allowFullviewSrcsetForPngs = false,
  afterImage,
  className,
  withoutZoomModal,
  isZoomed,
  onEnterZoomMode,
  onExitZoomMode,
}) => {
  // TODO: this is a hack and the Better Way would be to: break up this component
  const withZoomModal = !withoutZoomModal;
  const isMounted = useIsMounted();

  const imgRef = useRef<HTMLImageElement | null>(null);
  const [playButtonShown, setPlayButtonShown] = useState(true);
  const { previewFile, srcFile, isPixel, canZoom, src, srcSet, sizes, isLargeGif } = useFullview({
    stageWidth: width ?? 0,
    stageHeight: height ?? 0,
    source,
    allowFullviewSrcsetForPngs,
  });

  const handleClick = useCallback(
    e => {
      if (isLargeGif && imgRef.current && playButtonShown) {
        imgRef.current.srcset = ''; // force the src to show
        setPlayButtonShown(false);
        return;
      }
      if (canZoom && onEnterZoomMode) {
        // This is for INP purposes
        yieldToMain(() => {
          onEnterZoomMode(source);
        });
      }
    },
    [isLargeGif, playButtonShown, canZoom, onEnterZoomMode, source]
  );

  const handleCloseZoom = useCallback(() => {
    onExitZoomMode && onExitZoomMode(source);
  }, [onExitZoomMode, source]);

  // todo: all this state might be simplified down to just checking .complete
  // allows you to switch sources while 1) dimming out an image that's still loading
  // and 2) only updating the img dimensions until after the image has loaded
  const [imgSize, setImgSize] = useState<Dims | undefined>(() =>
    previewFile ? scaleToSize(previewFile, { width, height }) : undefined
  );
  const { isLoading, onSrcLoadFinish } = useImgIsLoading(imgRef, previewFile?.src);
  // if not loading, check for dimension changes and update size
  useEffect(() => {
    previewFile &&
      // on ios safari, this useEffect won't fire in certain cases when only checking
      // isLoading - so we check complete here instead (but still run the effect when
      // isLoading changes)
      imgRef.current?.complete &&
      setImgSize(scaleToSize(previewFile, { width, height }));
  }, [previewFile, imgRef.current?.complete, isLoading, width, height]);

  if (!previewFile || !srcFile) {
    return null;
  }

  return (
    <div
      className={classnames(s['root'], className)}
      style={dontExpandWrapper ? undefined : { width, height }}
      {...wrapperProps}
    >
      {withZoomModal && isZoomed && srcFile && (
        <ZoomModal
          src={srcFile.src}
          // note: still uses this deprecated version
          srcSet={getSrcSet(source)}
          width={srcFile.width}
          height={srcFile.height}
          isPixel={!!isPixel}
          onClose={handleCloseZoom}
        />
      )}
      {/* TODO: move large gif out of this base */}
      {isLargeGif && playButtonShown && (
        <div className={s['gif-player']} style={imgSize}>
          <IconButton
            icon={AddVideoIcon}
            size="large"
            onClick={handleClick}
            aria-label="Play Gif"
          />

          <div>Play large GIF ({numeral(srcFile.filesize).format('0.00b')})</div>
        </div>
      )}
      <img
        ref={imgRef}
        alt={altText}
        className={classnames(
          s['image'],
          isPixel && s['pixel'],
          canZoom && s['zoomable'],
          canZoom && isZoomed && s['zoomed'],
          isLoading && isMounted && s['loading']
        )}
        src={src}
        srcSet={srcSet}
        sizes={sizes}
        style={imgSize}
        onClick={handleClick}
        onLoad={onSrcLoadFinish}
        onError={onSrcLoadFinish}
        {...imageProps}
      />
      {afterImage}
    </div>
  );
};
Image.displayName = 'Image';

export default Image;
